/**
 * React Query mutation hooks for image operations
 * Wraps image mutations (generate, upload) with React Query
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import imageKeys from '@/api/querry_keys/imageKeys';
import { updateImageMetadata, deleteImage, uploadImage, generateImage } from '@/api/imageApi';
import { 
  cancelQueriesForResource, 
  getQueriesSnapshot, 
  updateItemInData, 
  applyOptimisticUpdate, 
  rollbackQueries 
} from './shared-mutation-utils';

/**
 * Hook for generating an image
 */
interface GenerateImageParams {
  prompt: string;
  model: string;
  aspectRatio?: string;
  width?: number;
  height?: number;
}

export const useGenerateImage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: GenerateImageParams) => {
      return await generateImage(params);
    },
    onSuccess: () => {
      // Invalidate image lists to show the new generating image
      queryClient.invalidateQueries({ queryKey: imageKeys.all });
    },
  });
};

/**
 * Hook for uploading an image
 */
interface UploadImageParams {
  image: File;
  prompt: string;
  model: string;
}

export const useUploadImage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ image, prompt, model }: UploadImageParams) => {
      return await uploadImage(image, prompt, model);
    },
    onSuccess: () => {
      // Invalidate image lists to show the new uploaded image
      queryClient.invalidateQueries({ queryKey: imageKeys.all });
    },
  });
};

/**
 * Hook for updating image metadata (admin only) with optimistic updates
 */
interface UpdateImageParams {
  imageId: string;
  status?: string;
  feedback?: string;
  title?: string;
  delete_request?: boolean;
  previousStatus?: string;
  userEmail?: string;
  adminEmail?: string;
}

export const useUpdateImageMetadata = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ imageId, status, feedback, title, delete_request }: UpdateImageParams) => {
      return await updateImageMetadata(imageId, status, feedback?.trim() || undefined, delete_request, title?.trim());
    },
    onMutate: async ({ imageId, status, feedback, title, delete_request, previousStatus }: UpdateImageParams) => {
      // Cancel outgoing refetches for ALL image-related queries
      await cancelQueriesForResource(queryClient, 'images');
      
      // Snapshot previous value for ALL image queries
      const previousData = getQueriesSnapshot(queryClient, 'images');
      
      // Optimistically update ALL image queries (including infinite scroll, lists, pinned, etc.)
      // Only include defined fields to avoid overwriting existing data with undefined
      const updates: Record<string, any> = {};
      if (status !== undefined) updates.status = status;
      if (feedback !== undefined) updates.feedback = feedback;
      if (title !== undefined) updates.title = title;
      if (delete_request !== undefined) updates.delete_request = delete_request;
      
      const updater = updateItemInData(imageId, updates);
      applyOptimisticUpdate(queryClient, 'images', updater);
      
      // Return context for rollback
      return { previousData, previousStatus };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        rollbackQueries(queryClient, context.previousData);
      }
    },
    onSuccess: () => {
      // Invalidate ALL image queries to trigger refetch and ensure consistency
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          return query.queryKey.length > 0 && query.queryKey[0] === 'images';
        },
        refetchType: 'none' // Don't refetch, just mark as stale
      });
    },
    onSettled: () => {
      // Final consistency check - invalidate all status count queries
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey;
          return queryKey.length > 0 && 
                 queryKey[0] === 'images' && 
                 queryKey.includes('status') && 
                 queryKey.includes('counts');
        }
      });
    },
  });
};

/**
 * Hook for deleting an image (admin only) with optimistic updates
 */
export const useDeleteImage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (imageId: string) => {
      return await deleteImage(imageId);
    },
    onMutate: async (imageId: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: imageKeys.all });
      
      // Snapshot previous value
      const previousData = queryClient.getQueriesData({ queryKey: imageKeys.all });
      
      // Helper to filter out deleted image
      const filterDeletedImage = (images: any[] | undefined) => 
        images?.filter((img: any) => img.id !== imageId) ?? [];
      
      // Helper to remove image from data
      const removeImageFromData = (oldData: any) => {
        if (!oldData) return oldData;
        
        if (oldData.pages) {
          // Infinite query format
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              images: filterDeletedImage(page.images),
              total_count: Math.max(0, (page.total_count ?? 0) - 1),
            })),
          };
        } else if (oldData.images) {
          // Regular list format
          return {
            ...oldData,
            images: filterDeletedImage(oldData.images),
            total_count: Math.max(0, (oldData.total_count ?? 0) - 1),
          };
        }
        return oldData;
      };
      
      // Optimistically remove from all image queries
      queryClient.setQueriesData({ queryKey: imageKeys.all }, removeImageFromData);
      
      // Return context for rollback
      return { previousData };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        rollbackQueries(queryClient, context.previousData);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: imageKeys.all });
      queryClient.invalidateQueries({ queryKey: ['imageStatusCounts'] });
    },
  });
};

/**
 * Combined hook for all image mutations
 * Provides easy access to all image mutation operations
 */
export const useImageMutations = () => {
  const generateMutation = useGenerateImage();
  const uploadMutation = useUploadImage();
  const updateMutation = useUpdateImageMetadata();
  const deleteMutation = useDeleteImage();

  return {
    // Mutation functions
    generateImage: generateMutation.mutate,
    generateImageAsync: generateMutation.mutateAsync,
    uploadImage: uploadMutation.mutate,
    uploadImageAsync: uploadMutation.mutateAsync,
    updateImage: updateMutation.mutate,
    updateImageAsync: updateMutation.mutateAsync,
    deleteImage: deleteMutation.mutate,
    deleteImageAsync: deleteMutation.mutateAsync,

    // Loading states
    isGenerating: generateMutation.isPending,
    isUploading: uploadMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Error states
    generateError: generateMutation.error,
    uploadError: uploadMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,

    // Success states
    generateSuccess: generateMutation.isSuccess,
    uploadSuccess: uploadMutation.isSuccess,
    updateSuccess: updateMutation.isSuccess,
    deleteSuccess: deleteMutation.isSuccess,

    // Reset functions
    resetGenerate: generateMutation.reset,
    resetUpload: uploadMutation.reset,
    resetUpdate: updateMutation.reset,
    resetDelete: deleteMutation.reset,

    // Raw mutation objects for advanced use
    generateMutation,
    uploadMutation,
    updateMutation,
    deleteMutation,
  };
};
